import { FC, useEffect, useMemo, useState } from 'react';
import { MultiEmailChipsInput } from '@components';
import { InitialAccess } from '../../inviteUserModal';
import { useUserAccessFormState } from '../../hooks/useUserAccessFormState';
import InviteStatusModal, { InviteEmailResults } from './InviteStatusModal';
import UserAccessDialogLayout from '../UserAccessDialogLayout';
import UserAccessForm from '../UserAccessForm';
import { CLIENT_USER_ROLE, UserAccess } from '@api/types/client-user-management';

const INVITE_USERS_DIALOG_COPY = {
  title: 'Invite Users',
  getSubmitLabel: (emailCount: number) => (emailCount > 0 ? `Invite (${emailCount})` : 'Invite'),
  submittingLabel: 'Inviting...',
};

interface InviteUserModalProps {
  open: boolean;
  dashboardsList: UserAccess[];
  clientCode: string;
  onClose: () => void;
  onSuccess: (results: InviteEmailResults) => void;
  initialAccess?: InitialAccess;
  companyName?: string;
  isLoadingDashboards?: boolean;
}

const InviteUserModal: FC<InviteUserModalProps> = ({
  open,
  dashboardsList,
  clientCode,
  onClose,
  onSuccess,
  initialAccess,
  companyName,
  isLoadingDashboards = false,
}) => {
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [emailsTouched, setEmailsTouched] = useState(false);
  const [emailsError, setEmailsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    selectedRole,
    setSelectedRole,
    selectedDashboardIds,
    dashboardAccordionExpanded,
    setDashboardAccordionExpanded,
    handleDashboardToggle,
    selectAllDashboards,
    clearAllDashboards,
    dashboardOptions,
  } = useUserAccessFormState({
    open,
    initialRole: initialAccess?.companyRole,
    initialDashboardIds: initialAccess?.submoduleIds,
    dashboardsList,
  });

  const { title, getSubmitLabel, submittingLabel } = INVITE_USERS_DIALOG_COPY;

  const submitButtonText = isSubmitting ? submittingLabel : getSubmitLabel(inviteEmails.length);
  const isDialogOpen = open && !showSuccessModal;

  const isSubmitEnabled = useMemo(() => {
    if (inviteEmails.length === 0 || isSubmitting) return false;
    if (selectedRole === CLIENT_USER_ROLE.MEMBER && selectedDashboardIds.length === 0) return false;
    return true;
  }, [inviteEmails, isSubmitting, selectedRole, selectedDashboardIds]);

  useEffect(() => {
    if (!open) {
      setInviteEmails([]);
      setEmailsTouched(false);
      setEmailsError(false);
      setIsSubmitting(false);
      setShowSuccessModal(false);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSuccessClose = (results: InviteEmailResults) => {
    setShowSuccessModal(false);
    onSuccess(results);
    onClose();
  };

  const handleSubmit = async () => {
    setEmailsTouched(true);
    if (inviteEmails.length === 0) {
      setEmailsError(true);
      return;
    }
    setIsSubmitting(true);
    setShowSuccessModal(true);
  };

  return (
    <>
      <UserAccessDialogLayout
        open={isDialogOpen}
        title={companyName ? `${title} - ${companyName} : ${clientCode}` : title}
        submitLabel={submitButtonText}
        canSubmit={isSubmitEnabled && !isLoadingDashboards}
        isSubmitting={isSubmitting}
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        <MultiEmailChipsInput
          open={open}
          label="Invite by Email"
          value={inviteEmails}
          onChange={setInviteEmails}
          showError={emailsTouched && emailsError}
          onTouched={() => setEmailsTouched(true)}
          onClearError={() => setEmailsError(false)}
          disabled={isSubmitting}
          placeholder="Enter comma-separated emails to invite"
        />
        <UserAccessForm
          selectedRole={selectedRole}
          dashboardOptions={dashboardOptions}
          selectedDashboardIds={selectedDashboardIds}
          dashboardAccordionExpanded={dashboardAccordionExpanded}
          onSelectRole={setSelectedRole}
          onToggleDashboard={handleDashboardToggle}
          onSelectAllDashboards={selectAllDashboards}
          onClearAllDashboards={clearAllDashboards}
          onAccordionExpand={setDashboardAccordionExpanded}
          isLoadingDashboards={isLoadingDashboards}
        />
      </UserAccessDialogLayout>
      {showSuccessModal && (
        <InviteStatusModal
          open={showSuccessModal}
          clientCode={clientCode}
          inviteeEmails={inviteEmails}
          selectedRole={selectedRole}
          selectedDashboardIds={selectedDashboardIds}
          onClose={handleSuccessClose}
        />
      )}
    </>
  );
};

export default InviteUserModal;
